import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Download, FileSearch, Table2 } from 'lucide-react'
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist'
import workerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url'
import { UniverHost } from './UniverHost'
import type {
  Conflict,
  CandidateValue,
  Evidence,
  AIAdjudication,
  Inference,
  ParameterRecord,
  ProjectData,
  SourceFile,
  SourceManifestEntry,
  TraceSelection,
  ViewerLocator,
} from './types'
import { adjudicationFor, candidateValuesFor } from './workbookData'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

GlobalWorkerOptions.workerSrc = workerUrl

interface SourcePanelProps {
  project: ProjectData
  selection: TraceSelection | null
  onEvidenceChange: (evidenceId: string) => void
}

interface ResolvedEvidence extends Evidence {
  source_file: SourceManifestEntry | null
  viewer_locator: ViewerLocator
  original_locator: ViewerLocator
}

export function SourcePanel({ project, selection, onEvidenceChange }: SourcePanelProps) {
  const record = useMemo(
    () => selection ? project.provenance.parameter_records.find((item) => item.record_id === selection.recordId) || null : null,
    [project, selection],
  )
  const candidates = record ? candidateValuesFor(record, project.provenance) : []
  const evidenceList = useMemo<ResolvedEvidence[]>(() => {
    if (!record) return []
    const sourceMap = new Map(project.viewerIndex.sources.map((item) => [item.source_file_id, item]))
    const decision = adjudicationFor(record, project.provenance)
    const selected = decision?.selected_candidate_id ? candidates.find((item) => item.candidate_id === decision.selected_candidate_id) : undefined
    const evidenceIds = [...new Set([
      ...(selected?.evidence_ids || []),
      ...(record.evidence_ids || []),
      ...candidates.flatMap((candidate) => candidate.evidence_ids || []),
    ])]
    return evidenceIds.map((id) => {
      const raw = project.viewerIndex.evidence[id] || project.provenance.evidence.find((item) => item.evidence_id === id)
      if (!raw) return null
      const source = raw.source_file || sourceMap.get(raw.source_file_id) || null
      return {
        ...raw,
        source_file: source,
        original_locator: raw.original_locator || raw.locator || {},
        viewer_locator: raw.viewer_locator || raw.locator || {},
      }
    }).filter(Boolean) as ResolvedEvidence[]
  }, [project, record, candidates])

  const activeEvidenceId = selection?.evidenceId && evidenceList.some((item) => item.evidence_id === selection.evidenceId)
    ? selection.evidenceId
    : evidenceList[0]?.evidence_id
  const activeEvidence = evidenceList.find((item) => item.evidence_id === activeEvidenceId) || null
  const hasPdfFooter = Boolean(activeEvidence?.source_file && ['pdf', 'pdf-derived'].includes(activeEvidence.source_file.viewer_kind || inferKind(activeEvidence.source_file)))
  const inference = record?.inference_id ? project.provenance.inferences.find((item) => item.inference_id === record.inference_id) : null
  const conflicts = (record?.conflict_ids || []).map((id) => project.provenance.conflicts.find((item) => item.conflict_id === id)).filter(Boolean) as Conflict[]
  const adjudication = record ? adjudicationFor(record, project.provenance) : undefined

  if (!record || !activeEvidence) {
    return <section className="source-panel" aria-label="原文溯源"><EmptySourceState /></section>
  }

  return (
    <section className={cn('source-panel', hasPdfFooter && 'has-pdf-footer')} aria-label="原文溯源">
      <EvidencePicker evidenceList={evidenceList} activeEvidenceId={activeEvidenceId!} onChange={onEvidenceChange} />
      <div className="source-panel-scroll">
        <EvidenceViewer evidence={activeEvidence} record={record} />
        <TraceMetadata record={record} evidence={activeEvidence} inference={inference || null} conflicts={conflicts} adjudication={adjudication} candidates={candidates} onEvidenceChange={onEvidenceChange} />
      </div>
    </section>
  )
}

function EvidencePicker({ evidenceList, activeEvidenceId, onChange }: { evidenceList: ResolvedEvidence[]; activeEvidenceId: string; onChange: (evidenceId: string) => void }) {
  const [open, setOpen] = useState(false)
  const active = evidenceList.find((item) => item.evidence_id === activeEvidenceId)
  return <div className="evidence-picker">
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="evidence-picker-trigger">
          <span className="mono">{active?.evidence_id || '选择证据'}</span>
          <span className="evidence-picker-file">{shortFileName(active?.source_file?.file_name || '未知源文件')}</span>
          <ChevronDownIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="evidence-popover">
        <Command>
          <CommandInput placeholder="搜索 evidence_id 或文件名" />
          <CommandList>
            <CommandEmpty>没有匹配的证据</CommandEmpty>
            <CommandGroup heading={`${evidenceList.length} 条证据`}>
              {evidenceList.map((evidence) => <CommandItem key={evidence.evidence_id} value={`${evidence.evidence_id} ${evidence.source_file?.file_name || ''}`} onSelect={() => { onChange(evidence.evidence_id); setOpen(false) }}>
                <span className={cn('command-check', activeEvidenceId === evidence.evidence_id && 'is-active')} />
                <span className="evidence-command-copy"><strong className="mono">{evidence.evidence_id}</strong><small>{shortFileName(evidence.source_file?.file_name || '未知源文件')}</small></span>
              </CommandItem>)}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  </div>
}

function ChevronDownIcon() {
  return <ChevronDown className="chevron-down" aria-hidden="true" />
}

function EvidenceViewer({ evidence, record }: { evidence: ResolvedEvidence; record: ParameterRecord }) {
  const source = evidence.source_file
  if (!source) return <div className="viewer-empty">源文件清单中没有找到该文件。</div>
  const kind = source.viewer_kind || inferKind(source)
  if ((kind === 'pdf' || kind === 'pdf-derived') && source.viewer_url) {
    return <PdfViewer key={evidence.evidence_id} url={source.viewer_url} locator={evidence.viewer_locator} record={record} />
  }
  if (kind === 'xlsx' && source.snapshot_url) {
    return <XlsxViewer key={evidence.evidence_id} url={source.snapshot_url} locator={evidence.viewer_locator} />
  }
  if (kind === 'image' && source.viewer_url) {
    return <ImageViewer url={source.viewer_url} locator={evidence.viewer_locator} />
  }
  return <DownloadViewer source={source} />
}

function PdfViewer({ url, locator, record }: { url: string; locator: ViewerLocator; record: ParameterRecord }) {
  const pageHostRef = useRef<HTMLDivElement>(null)
  const [pdf, setPdf] = useState<any>(null)
  const [page, setPage] = useState(Math.max(1, (locator.page_index ?? locator.page_number ? Number(locator.page_index ?? Number(locator.page_number) - 1) : 0) + 1))
  const [pageCount, setPageCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    setPdf(null)
    getDocument({ url }).promise.then((document) => {
      if (!alive) return
      setPdf(document)
      setPageCount(document.numPages)
      const target = locator.page_index != null ? locator.page_index + 1 : locator.page_number || 1
      setPage(Math.min(Math.max(1, target), document.numPages))
      setLoading(false)
    }).catch((reason) => {
      if (!alive) return
      setError(reason instanceof Error ? reason.message : String(reason))
      setLoading(false)
    })
    return () => { alive = false }
  }, [url, locator])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return
      if (event.key === 'ArrowLeft') setPage((value) => Math.max(1, value - 1))
      if (event.key === 'ArrowRight') setPage((value) => Math.min(pageCount || value, value + 1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pageCount])

  useEffect(() => {
    if (!pdf || !pageHostRef.current) return
    let cancelled = false
    let renderTask: any
    const render = async () => {
      const pageObject = await pdf.getPage(page)
      if (cancelled || !pageHostRef.current) return
      const baseViewport = pageObject.getViewport({ scale: 1 })
      const width = Math.max(360, pageHostRef.current.clientWidth - 28)
      const scale = Math.min(1.65, Math.max(0.68, width / baseViewport.width))
      const viewport = pageObject.getViewport({ scale })
      const pixelRatio = window.devicePixelRatio || 1
      const canvas = pageHostRef.current.querySelector('canvas') as HTMLCanvasElement
      const canvasContext = canvas.getContext('2d')
      if (!canvasContext) return
      canvas.width = Math.floor(viewport.width * pixelRatio)
      canvas.height = Math.floor(viewport.height * pixelRatio)
      canvas.style.width = `${viewport.width}px`
      canvas.style.height = `${viewport.height}px`
      renderTask = pageObject.render({
        canvasContext,
        viewport,
        transform: pixelRatio !== 1 ? [pixelRatio, 0, 0, pixelRatio, 0, 0] : undefined,
      })
      await renderTask.promise
      if (cancelled) return
      pageHostRef.current.style.setProperty('--page-width', `${viewport.width}px`)
      pageHostRef.current.style.setProperty('--page-height', `${viewport.height}px`)
      pageHostRef.current.style.setProperty('--page-scale', String(scale))
    }
    render().catch((reason) => { if (!cancelled) setError(reason instanceof Error ? reason.message : String(reason)) })
    return () => { cancelled = true; renderTask?.cancel?.() }
  }, [pdf, page, locator])

  const regions = locator.regions || []

  return (
    <div className="source-viewer pdf-viewer">
      {loading ? <div className="viewer-loading">加载中…</div> : null}
      {error ? <div className="viewer-error">PDF 加载失败：{error}</div> : null}
      <div className="page-stage" ref={pageHostRef}>
        <div className="pdf-page" style={{ width: 'var(--page-width)', height: 'var(--page-height)' }}>
          <canvas />
          {regions.map((region, index) => {
            const box = region.bbox_pt || (region.bbox_norm && locator.page_size_pt ? {
              x: region.bbox_norm.x * locator.page_size_pt.width,
              y: region.bbox_norm.y * locator.page_size_pt.height,
              width: region.bbox_norm.width * locator.page_size_pt.width,
              height: region.bbox_norm.height * locator.page_size_pt.height,
            } : null)
            if (!box) return null
            return <div className="bbox-highlight" key={`${index}-${box.x}`} style={{ left: `calc(${box.x}px * var(--page-scale))`, top: `calc(${box.y}px * var(--page-scale))`, width: `calc(${box.width}px * var(--page-scale))`, height: `calc(${box.height}px * var(--page-scale))` }} />
          })}
        </div>
      </div>
      <div className="viewer-toolbar">
        <div className="viewer-pagination">
          <Button variant="outline" size="icon" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page <= 1} aria-label="上一页"><ChevronLeft /></Button>
          <span className="page-current" aria-label={`第${page}页`}>{page}</span>
          <span>/ {pageCount || '—'}</span>
          <Button variant="outline" size="icon" onClick={() => setPage((value) => Math.min(pageCount || value, value + 1))} disabled={pageCount > 0 && page >= pageCount} aria-label="下一页"><ChevronRight /></Button>
        </div>
          <div className="viewer-facts" aria-label="记录结果">
          <div className="viewer-fact">
            <span>参数值</span>
            <strong title={formatValue(record.ai_recommended_value ?? record.final_value ?? record.raw_value)}>{formatValue(record.ai_recommended_value ?? record.final_value ?? record.raw_value)}</strong>
            {record.unit ? <small>{record.unit}</small> : null}
          </div>
          <div className="viewer-fact">
            <span>置信度</span>
            <strong>{record.confidence != null ? `${Math.round(record.confidence * 100)}%` : '—'}</strong>
          </div>
        </div>
      </div>
    </div>
  )
}

function XlsxViewer({ url, locator }: { url: string; locator: ViewerLocator }) {
  const [snapshot, setSnapshot] = useState<Record<string, unknown> | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    fetch(url).then((response) => response.json()).then((data) => { if (alive) setSnapshot(data) }).catch((reason) => { if (alive) setError(String(reason)) })
    return () => { alive = false }
  }, [url])
  if (error) return <div className="viewer-error">XLSX 快照加载失败：{error}</div>
  if (!snapshot) return <div className="viewer-loading">加载中…</div>
  return (
    <div className="source-viewer xlsx-viewer">
      <div className="viewer-caption"><Table2 /> <span>原始工作表：<strong>{locator.sheet_name || '未指定'}</strong> · <span className="mono">{locator.cell_range || '未指定单元格'}</span></span></div>
      <UniverHost workbookData={snapshot} activeSheet={locator.sheet_name} sourceCell={{ sheetName: locator.sheet_name, cellRange: locator.cell_range }} />
    </div>
  )
}

function ImageViewer({ url, locator }: { url: string; locator: ViewerLocator }) {
  return (
    <div className="source-viewer image-viewer">
      <img src={url} alt={locator.text_quote?.exact || '源文件图片'} />
    </div>
  )
}

function DownloadViewer({ source }: { source: SourceManifestEntry }) {
  return (
    <div className="source-viewer unsupported-viewer">
      <div className="empty-icon"><Download /></div>
      <strong>此格式保留原文件定位</strong>
      <p>{source.file_name}</p>
      {source.original_url ? <a href={source.original_url} target="_blank" rel="noreferrer">打开原文件</a> : <span className="muted">当前交付包未包含原文件副本</span>}
    </div>
  )
}

function TraceMetadata({ record, evidence, inference, conflicts, adjudication, candidates, onEvidenceChange }: { record: ParameterRecord; evidence: ResolvedEvidence; inference: Inference | null; conflicts: Conflict[]; adjudication?: AIAdjudication; candidates: CandidateValue[]; onEvidenceChange: (evidenceId: string) => void }) {
  const locator = evidence.original_locator || evidence.locator
  return (
    <div className="metadata-stack">
      {adjudication ? <AIAdjudicationPanel adjudication={adjudication} candidates={candidates} onEvidenceChange={onEvidenceChange} /> : null}
      <Accordion type="multiple" defaultValue={['locator']} className="metadata-accordion">
        <AccordionItem value="locator" className="metadata-item">
          <AccordionTrigger className="metadata-trigger"><span><FileSearch />定位元数据</span><Badge variant="secondary">可跳转</Badge></AccordionTrigger>
          <AccordionContent className="metadata-content">
            <MetaRow label="record_id" value={record.record_id} mono />
            <MetaRow label="parameter_id" value={record.parameter_id} mono />
            <MetaRow label="evidence_id" value={evidence.evidence_id} mono />
            <MetaRow label="源文件" value={`${evidence.source_file?.source_file_id || evidence.source_file_id} · ${evidence.source_file?.file_name || '未知'}`} />
            <MetaRow label="版本/范围" value={`${evidence.source_version || evidence.source_file?.document_revision || '未指定'} / ${evidence.source_file?.scope || '未指定'}`} />
            <LocatorDetails locator={locator} viewerLocator={evidence.viewer_locator} />
            <MetaRow label="原文引用" value={evidence.quote || locator?.text_quote?.exact || '未提供'} quote />
          </AccordionContent>
        </AccordionItem>

        {inference ? <AccordionItem value="inference" className="metadata-item inference-item">
          <AccordionTrigger className="metadata-trigger"><span><FileSearch />推理链</span><Badge variant="outline">{inference.inference_id}</Badge></AccordionTrigger>
          <AccordionContent className="metadata-content">
            <MetaRow label="标准/方法" value={inference.standard || '未指定'} />
            {inference.formula ? <MetaRow label="公式" value={inference.formula} mono /> : null}
            <div className="inference-chain">{(inference.chain || []).map((step, index) => <div key={`${index}-${step}`}><span>{index + 1}</span>{step}</div>)}</div>
            {inference.cross_validation ? <MetaRow label="交叉验证" value={inference.cross_validation} /> : null}
          </AccordionContent>
        </AccordionItem> : null}

        {conflicts.length && !adjudication ? <AccordionItem value="conflicts" className="metadata-item conflict-item">
          <AccordionTrigger className="metadata-trigger"><span><AlertTriangle />冲突候选</span><Badge variant="outline">{conflicts.length}</Badge></AccordionTrigger>
          <AccordionContent className="metadata-content">
            {conflicts.map((conflict) => <div className="conflict-block" key={conflict.conflict_id}>
              <div className="conflict-id">{conflict.conflict_id} · {conflict.resolution || '待确认'}</div>
              {(conflict.values || []).map((value, index) => <div className="conflict-value" key={`${conflict.conflict_id}-${index}`}><span>{value.version || value.revision || value.source_file_id || '候选值'}</span><strong>{formatValue(value.value)}</strong></div>)}
              {conflict.reason ? <p>{conflict.reason}</p> : null}
            </div>)}
          </AccordionContent>
        </AccordionItem> : null}
      </Accordion>
    </div>
  )
}

function AIAdjudicationPanel({ adjudication, candidates, onEvidenceChange }: { adjudication: AIAdjudication; candidates: CandidateValue[]; onEvidenceChange: (evidenceId: string) => void }) {
  const assessmentById = new Map(adjudication.candidate_assessments.map((item) => [item.candidate_id, item]))
  return <section className="ai-adjudication-panel" aria-label="AI候选值裁决">
    <div className="ai-adjudication-heading">
      <div><span className="metadata-eyebrow">AI 判定</span><strong>{adjudication.decision_state}</strong></div>
      <Badge variant={adjudication.decision_state === 'AI已采用' ? 'default' : 'outline'}>{Math.round(adjudication.confidence * 100)}%</Badge>
    </div>
    <p className="ai-adjudication-summary">{adjudication.summary}</p>
    <div className="candidate-list" aria-label="全部候选值">
      {candidates.map((candidate) => {
        const assessment = assessmentById.get(candidate.candidate_id)
        const selected = candidate.candidate_id === adjudication.selected_candidate_id
        const evidenceId = candidate.evidence_ids?.[0]
        return <button
          type="button"
          className={cn('candidate-row', selected && 'is-selected', candidate.eligibility === '排除' && 'is-excluded')}
          key={candidate.candidate_id}
          disabled={!evidenceId}
          onClick={() => evidenceId && onEvidenceChange(evidenceId)}
          title={evidenceId ? '打开该候选对应证据' : '当前候选没有可核查证据'}
        >
          <span className="candidate-marker">{selected ? <CheckCircle2 /> : <span>{candidates.indexOf(candidate) + 1}</span>}</span>
          <span className="candidate-copy"><strong>{formatValue(candidate.value)}</strong><small>{[candidate.revision && `版本 ${candidate.revision}`, assessment?.result, candidate.evidence_ids?.length ? `${candidate.evidence_ids.length} 条证据` : '无证据'].filter(Boolean).join(' · ')}</small></span>
          {selected ? <Badge variant="secondary">推荐</Badge> : null}
        </button>
      })}
    </div>
    <div className="ai-reasoning-list" aria-label="AI判定过程">
      {adjudication.reasoning_steps.map((step, index) => <div className="ai-reasoning-step" key={`${step.step}-${index}`}><span>{index + 1}</span><div><strong>{step.step}</strong><p>{step.statement}</p></div></div>)}
    </div>
    {adjudication.uncertainty?.length ? <div className="ai-uncertainty"><strong>待确认</strong><p>{adjudication.uncertainty.join('；')}</p></div> : null}
  </section>
}

function LocatorDetails({ locator, viewerLocator }: { locator?: ViewerLocator; viewerLocator?: ViewerLocator }) {
  if (!locator) return null
  const isPdf = locator.kind === 'pdf' || viewerLocator?.kind === 'pdf'
  return <div className="locator-details">
    <MetaRow label="定位类型" value={locator.kind || '未知'} />
    {isPdf ? <>
      <MetaRow label="页码" value={`${locator.page_number ?? '—'}（index ${locator.page_index ?? '—'}）`} />
      <MetaRow label="页面尺寸" value={locator.page_size_pt ? `${locator.page_size_pt.width} × ${locator.page_size_pt.height} pt` : '未提供'} mono />
      <MetaRow label="坐标系" value={locator.coordinate_system || '未提供'} mono />
      <MetaRow label="BBOX" value={locator.regions?.length ? JSON.stringify(locator.regions.map((item) => item.bbox_pt || item.bbox_norm)) : '无可用区域'} mono quote />
      {viewerLocator?.derived ? <MetaRow label="查看器定位" value={`派生定位 · ${viewerLocator.derivation_method || '构建阶段转换'}`} /> : null}
    </> : null}
    {locator.kind === 'xlsx' ? <>
      <MetaRow label="工作表" value={locator.sheet_name || '—'} />
      <MetaRow label="单元格范围" value={locator.cell_range || '—'} mono />
      <MetaRow label="行/列" value={`${locator.row ?? '—'} / ${locator.column ?? '—'}`} mono />
    </> : null}
    {locator.kind === 'docx' ? <>
      <MetaRow label="章节" value={locator.section || '—'} />
      <MetaRow label="段落/表格" value={`${locator.paragraph_index ?? '—'} / ${locator.table_index ?? '—'}`} mono />
      <MetaRow label="行/列" value={`${locator.row ?? '—'} / ${locator.column ?? '—'}`} mono />
    </> : null}
  </div>
}

function MetaRow({ label, value, mono = false, quote = false }: { label: string; value: string; mono?: boolean; quote?: boolean }) {
  return <div className={`meta-row ${quote ? 'meta-quote' : ''}`}><span>{label}</span><strong className={mono ? 'mono' : ''}>{value}</strong></div>
}

function EmptySourceState() {
  return <div className="source-empty-state" aria-hidden="true" />
}

function formatValue(value: unknown) {
  if (value == null || value === '') return '—'
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

function shortFileName(value: string) {
  return value.length > 42 ? `${value.slice(0, 18)}…${value.slice(-20)}` : value
}

function inferKind(source: SourceFile) {
  const mediaType = String(source.media_type || '').toLowerCase()
  const extension = source.file_name?.split('.').pop()?.toLowerCase()
  if (mediaType.includes('pdf') || extension === 'pdf') return 'pdf'
  if (mediaType.includes('spreadsheet') || extension === 'xlsx') return 'xlsx'
  if (mediaType.startsWith('image/')) return 'image'
  return 'download-only'
}
